import {
  Box,
  Flex,
  InputGroup,
  Link,
  RadioGroup,
  Text,
} from '@clear/design-system'
import { Map } from '@components/storybook'
import { LocationError } from '@pages/enroll/locations'
import {
  EnrollmentLocation,
  LocationCoordinates,
} from '@utils/getNearbyEnrollmentLocations'
import React, { ChangeEvent } from 'react'
import { GeoLocate } from '../GeoLocate'
import { LocationInfo } from '../LocationInfo'

interface V1LocationsSearch {
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleSearch: (searchCriteria: string) => Promise<void>
  updateSearchBar: (e: ChangeEvent<HTMLInputElement>) => void
  searchInput: string
  geoLocateSuccessHandler: (position: GeolocationPosition) => void
  mapSearchError: boolean
  locationsWithinRange: EnrollmentLocation[]
  errors: LocationError
  isLoaded: boolean
  searchLocation: LocationCoordinates
  determineMapZoomLevel: (locationsWithinRange: EnrollmentLocation[]) => number
  isSearchComplete: boolean
  handleSelect?: (location: string) => void
  selectionKey?: 'locationCode' | 'serviceTerritoryId'
}

const V1LocationsSearch = ({
  handleKeyDown,
  handleSearch,
  updateSearchBar,
  searchInput,
  geoLocateSuccessHandler,
  mapSearchError,
  locationsWithinRange,
  errors,
  isLoaded,
  searchLocation,
  determineMapZoomLevel,
  isSearchComplete,
  handleSelect,
  selectionKey = 'locationCode',
}: V1LocationsSearch) => {
  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="center"
        mb={7}
        width="full"
        flexDirection={['column', 'row']}
      >
        <InputGroup
          icon="search"
          onKeyDown={(e) => {
            handleKeyDown(e)
          }}
          variant="default"
          iconClick={() => handleSearch(searchInput)}
          onChange={updateSearchBar}
          value={searchInput}
          placeholder="Zip Code / City / Airport Code"
          autoComplete="off"
          data-cy="searchBar"
        />
        <GeoLocate onSuccessHandler={geoLocateSuccessHandler} />
      </Flex>
      {mapSearchError ? (
        <Box as="p" textAlign="center" color="utilityDarkErrorRed">
          Sorry, that's not a valid search. Please try again.
        </Box>
      ) : null}
      <Box as="p" textAlign="center" color="utilityDarkErrorRed">
        {locationsWithinRange.length === 0
          ? errors?.enrollmentLocationId?.length
          : null}
      </Box>

      {locationsWithinRange?.length ? (
        <Flex flexDirection={['column-reverse', 'row']}>
          <Box
            width={['full', '55%']}
            mr={4}
            height="500px"
            overflowY="scroll"
            pr={4}
          >
            <RadioGroup
              name="searchResult"
              data-cy="search-result-radio-group"
              options={locationsWithinRange.map(
                (location: EnrollmentLocation) => ({
                  key: location?.[selectionKey],
                  label: location?.name,
                  subLabel: (
                    <Box mt={1} minWidth="1.25rem" maxWidth="350px">
                      <Text
                        fontFamily="Inter-SemiBold"
                        variant="display"
                        fontSize="16px"
                      >
                        {location?.distance} miles
                      </Text>
                      <br />
                      <div className="h-1/2" />
                      {location && (
                        <LocationInfo
                          key={location?.locationCode}
                          location={location}
                        />
                      )}
                    </Box>
                  ),
                  value: location?.[selectionKey],
                })
              )}
              onChange={(e) => handleSelect(e?.target?.value)}
              containerProps={{
                flexDirection: 'column',
                flexShrink: 0,
                alignItems: 'start',
                display: 'flex-end',
              }}
            />
          </Box>
          {isLoaded && searchLocation ? (
            <Box
              width={['full', '50%']}
              height={['400px', '500px']}
              mb={[6, 0]}
            >
              <Map
                center={searchLocation}
                zoom={determineMapZoomLevel(locationsWithinRange)}
                locationMarkers={locationsWithinRange}
                full
              />
            </Box>
          ) : (
            <>Loading google maps</>
          )}
        </Flex>
      ) : null}

      {!locationsWithinRange.length &&
      searchLocation &&
      !mapSearchError &&
      isSearchComplete ? (
        <Flex alignItems="start" ml={4} mt={3}>
          <Box as="img" src="/map-pin-not-found.svg" alt="pin-not-found" />
          <Box ml={3}>
            <Text fontSize={4} display="block" mb={1}>
              No locations found
            </Text>
            <Text fontSize={1}>
              Please adjust your search for better results.
            </Text>
          </Box>
        </Flex>
      ) : null}

      {locationsWithinRange && locationsWithinRange[0]?.distance > 40 && (
        <Flex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          my={6}
        >
          <Text fontSize={4} display="block" mb={1}>
            Need a closer location?
          </Text>
          <Text fontSize={2}>
            Visit the{' '}
            <Link
              target="_blank"
              href="https://www.tsa.gov/precheck/enrollment-centers"
            >
              TSA website
            </Link>{' '}
            for more enrollment location options.
          </Text>
        </Flex>
      )}
    </>
  )
}

export default V1LocationsSearch
